import { TablePage, Dialog, GroupList } from 'web-mojo';
import { Machine, MachineList } from '@portal/models/Machine.js';
import './TerminalView.js';

export default class VendingMachinesPage extends TablePage {
    constructor(options = {}) {
        super({
            title: 'Terminals',
            icon: 'bi-shop',
            pageName: 'vending_machines',
            route: 'group/vending/machines',
            permissions: ['view_payments', 'manage_payments', 'payments', 'manage_group', 'manage_groups'],
            requiresGroup: true,

            Collection: MachineList,

            defaultQuery: {
                sort: 'name',
                group: () => app.activeGroup?.id,
            },

            columns: [
                {
                    label: 'Terminal',
                    key: 'name',
                    formatter: "default('—')",
                },
                {
                    label: 'UUID',
                    key: 'uuid',
                    filter: {
                        type: 'input',
                    }
                },
                {
                    label: 'Location',
                    key: 'location.name',
                    formatter: "default('—')",
                    visibility: 'md',
                },
                {
                    label: 'State',
                    key: 'activation_state',
                    formatter: "badge:pending=warning,active=success,suspended=secondary,revoked=danger",
                    filter: {
                        type: 'select',
                        options: [
                            { label: 'Pending',   value: 'pending' },
                            { label: 'Active',    value: 'active' },
                            { label: 'Suspended', value: 'suspended' },
                            { label: 'Revoked',   value: 'revoked' },
                        ]
                    },
                },
                {
                    label: 'Device',
                    key: 'device.name',
                    formatter: (val, row) => {
                        const dev = row?.device;
                        if (!dev) return '<span class="text-muted small">No lock</span>';
                        const dot = dev.is_online
                            ? '<i class="bi bi-circle-fill text-success me-1" style="font-size:.6rem;"></i>'
                            : '<i class="bi bi-circle text-secondary me-1" style="font-size:.6rem;"></i>';
                        return `${dot}${dev.name || dev.id}`;
                    },
                    visibility: 'md',
                },
                {
                    label: 'Last Seen',
                    key: 'last_seen',
                    formatter: 'relative',
                    visibility: 'lg',
                },
                {
                    label: 'QR Code',
                    key: 'qr_code_id',
                    formatter: (val) => val
                        ? `<span class="font-monospace small text-muted">${val}</span>`
                        : '<span class="text-muted small">—</span>',
                    visibility: 'xl',
                },
            ],

            viewDialogOptions: {
                header: false,
                size: 'lg',
                footer: false,
            },

            selectable: true,

            batchActions: [
                { label: 'Move', icon: 'bi bi-arrow-right', action: 'move' },
                { label: 'Activate', icon: 'bi bi-check-circle', action: 'activate' },
                { label: 'Suspend', icon: 'bi bi-pause-circle', action: 'suspend' },
                { label: 'Export', icon: 'bi bi-download', action: 'export' },
            ],

            searchable: true,
            sortable: true,
            filterable: true,
            paginated: true,
            showRefresh: true,
            addButtonLabel: 'Add Terminal',
            showExport: false,

            tableOptions: {
                striped: false,
                bordered: false,
                hover: true,
                responsive: true,
            },

            contextMenu: [
                { label: 'View Details',     icon: 'bi bi-eye', action: 'view' },
                { label: 'Activate',         icon: 'bi bi-check-circle', action: 'activate-terminal' },
                { label: 'Suspend',          icon: 'bi bi-pause-circle', action: 'suspend-terminal', className: 'text-warning' },
                { label: 'Admin Unlock',     icon: 'bi bi-unlock', action: 'unlock-terminal' },
                { label: 'Revoke',           icon: 'bi bi-x-circle', action: 'revoke-terminal', className: 'text-danger' },
            ],

            tableViewOptions: {
                hideActivePillNames: ['group'],
            },

            ...options,
        });
    }

    async onInit() {
        await super.onInit();
        app.events.on('message:lock', this._onDeviceStatus.bind(this));
    }

    onDestroy() {
        app.events.off('message:lock', this._onDeviceStatus.bind(this));
    }

    _onDeviceStatus(data) {
        // Update device status dot in-place without full re-fetch
        const deviceId = data?.device_id;
        if (!deviceId) return;
        const machine = this.collection.models.find(m => m.get('device')?.id === deviceId);
        if (!machine) return;
        const dev = machine.get('device');
        if (dev) {
            dev.is_online = data.is_online;
            dev.lock_engaged = data.lock_engaged;
            machine.set('device', dev);
        }
    }

    async onActionBatchMove() {
        const selected = this.tableView.getSelectedItems();
        if (!selected?.length) return;
        const data = await Dialog.showForm({
            title: `Move ${selected.length} terminal${selected.length > 1 ? 's' : ''}`,
            fields: [{
                type: 'collection',
                name: 'group',
                label: 'Target Group',
                Collection: GroupList,
                labelField: 'name',
                valueField: 'id',
                maxItems: 10,
                placeholder: 'Search groups...',
                emptyFetch: false,
                debounceMs: 300,
            }]
        });
        if (!data?.group) return;
        await Promise.all(selected.map(item => item.model.save({ group_id: parseInt(data.group) })));
        app.toast.success(`Moved ${selected.length} terminal${selected.length > 1 ? 's' : ''}`);
        this.tableView.collection.fetch();
    }

    async onActionBatchActivate() {
        const selected = this.tableView.getSelectedItems();
        if (!selected?.length) return;
        const confirmed = await Dialog.confirm({
            title: `Activate ${selected.length} terminal${selected.length > 1 ? 's' : ''}`,
            message: 'Activate selected terminals? They will be able to process transactions immediately.',
            confirmText: 'Activate',
            confirmClass: 'btn-success',
        });
        if (!confirmed) return;
        await Promise.all(selected.map(item => item.model.save({ activate: 1 })));
        app.toast.success(`Activated ${selected.length} terminal${selected.length > 1 ? 's' : ''}`);
        this.tableView.collection.fetch();
    }

    async onActionBatchSuspend() {
        const selected = this.tableView.getSelectedItems();
        if (!selected?.length) return;
        const confirmed = await Dialog.confirm({
            title: `Suspend ${selected.length} terminal${selected.length > 1 ? 's' : ''}`,
            message: 'Suspend selected terminals? They can be reactivated later.',
            confirmText: 'Suspend',
            confirmClass: 'btn-warning',
        });
        if (!confirmed) return;
        await Promise.all(selected.map(item => item.model.save({ suspend: 1 })));
        app.toast.success(`Suspended ${selected.length} terminal${selected.length > 1 ? 's' : ''}`);
        this.tableView.collection.fetch();
    }

    async onActionBatchExport() {
        this.tableView.collection.export();
    }

    async onActionActivateTerminal(event, el) {
        const id = el.getAttribute('data-id');
        const machine = this.collection.get(id);
        if (!machine) return;

        const state = machine.get('activation_state');
        if (state === 'active') {
            app.toast.info('Terminal is already active');
            return;
        }
        if (state === 'revoked') {
            app.toast.error('Revoked terminals cannot be reactivated');
            return;
        }

        const confirmed = await Dialog.confirm({
            title: 'Activate Terminal',
            message: `Activate "${machine.nameOrId()}"? The terminal will be able to process transactions immediately.`,
            confirmText: 'Activate',
            confirmClass: 'btn-success',
        });
        if (!confirmed) return;

        const response = await machine.save({ activate: 1 });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to activate terminal');
            return;
        }
        app.toast.success('Terminal activated');
        this.tableView.collection.fetch();
    }

    async onActionSuspendTerminal(event, el) {
        const id = el.getAttribute('data-id');
        const machine = this.collection.get(id);
        if (!machine) return;

        const confirmed = await Dialog.confirm({
            title: 'Suspend Terminal',
            message: `Suspend "${machine.nameOrId()}"? It can be reactivated later.`,
            confirmText: 'Suspend',
            confirmClass: 'btn-warning',
        });
        if (!confirmed) return;

        const response = await machine.save({ suspend: 1 });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to suspend terminal');
            return;
        }
        app.toast.success('Terminal suspended');
        this.tableView.collection.fetch();
    }

    async onActionUnlockTerminal(event, el) {
        const id = el.getAttribute('data-id');
        const machine = this.collection.get(id);
        if (!machine) return;

        if (!machine.get('device')) {
            app.toast.error('No lock device assigned to this terminal');
            return;
        }

        const confirmed = await Dialog.confirm({
            title: 'Admin Unlock',
            message: `Send an unlock command to "${machine.nameOrId()}"?`,
            confirmText: 'Unlock',
            confirmClass: 'btn-primary',
        });
        if (!confirmed) return;

        const response = await machine.save({
            unlock: { reason: 'portal admin unlock', ttl: 30 },
        });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to send unlock command');
            return;
        }
        app.toast.success('Unlock command dispatched');
    }

    async onActionRevokeTerminal(event, el) {
        const id = el.getAttribute('data-id');
        const machine = this.collection.get(id);
        if (!machine) return;

        const confirmed = await Dialog.confirm({
            title: 'Revoke Terminal',
            message: `Permanently revoke "${machine.nameOrId()}"? This cannot be undone.`,
            confirmText: 'Revoke',
            confirmClass: 'btn-danger',
        });
        if (!confirmed) return;

        const response = await machine.save({ revoke: 1 });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to revoke terminal');
            return;
        }
        app.toast.success('Terminal revoked');
        this.tableView.collection.fetch();
    }
}
